/**
 * `chorus-md` — husets meldingsformat, og de to pekerne som gjør det til noe annet enn markdown.
 *
 * =================================================================================================
 * TO FORMER, ÉN REGEL: LAGRE PEKEREN, VIS TEKSTEN.
 *
 *     @[[person:<uuid>|Ada Lovelace]]     en nevning — et VARSEL til et menneske
 *     #[[task:<uuid>|Rigge scene 2]]      en lenke   — et kort til et objekt
 *
 * Alternativet var å lagre «@Ada» og slå opp navnet når meldingen leses. Det er feil på tre måter,
 * og den tredje avgjør (avgjørelse 13):
 *
 *   1. To personer kan hete Ada. Et oppslag på navn må velge, og valget er usynlig.
 *   2. En som bytter navn gjør hver eldre nevning til en peker på ingenting.
 *   3. EN NEVNING ER ET VARSEL. «@Ada» skrevet i fritekst er en tekst; `@[[person:…|Ada]]` er noen
 *      som valgte Ada i velgeren. Bare det andre skal ringe hos noen, og bare det andre kan si hvem.
 *
 * TEKSTEN I PEKEREN ER ET ØYEBLIKKSBILDE, og det er med vilje: den viser navnet slik det var da
 * noen skrev det. Klienten står fritt til å tegne det ferske navnet fra `id`-en — men hvis den ikke
 * kan, står det som ble skrevet, og det er alltid bedre enn en tom firkant.
 *
 * =================================================================================================
 * HVORFOR DEN BOR I `packages/shared`.
 *
 * Serveren PARSER den — nevninger blir til `message_mentions`-rader og varsler — og klienten
 * RENDRER den. To parsere ville vært to tolkninger av den samme teksten, og den første uenigheten
 * er en nevning som varsler uten å vises, eller vises uten å varsle.
 *
 * Fila har ingen avhengigheter. Den kan kjøre i en nettleser, som hele pakken må.
 *
 * =================================================================================================
 * RUNDTUREN ER EN TEST, IKKE EN PÅSTAND.
 *
 * `segmentsOf(body)` deler kroppen i biter, og `bodyOf(segments)` setter dem sammen igjen. De to
 * skal gi nøyaktig den samme strengen for enhver kropp — også en med en ØDELAGT peker i seg, som
 * da er ren tekst. Det er den regelen `chorus-md.spec.ts` måler, og den er grunnen til at
 * parseren aldri kaster: en melding som er skrevet, skal kunne vises.
 */

/** Objektene en `#`-lenke kan peke på. Ordrens egne tre. */
export const LINK_OBJECT_TYPES = ['task', 'performance', 'resource'] as const;
export type LinkObjectType = (typeof LINK_OBJECT_TYPES)[number];

/**
 * En bit av en kropp.
 *
 * `text` bærer teksten ordrett; `mention` og `link` bærer pekeren OG etiketten som sto der.
 * Klienten tegner de to siste som chips, og `label` er reserven når id-en ikke lar seg slå opp.
 */
export type ChorusMdSegment =
  | { kind: 'text'; text: string }
  | { kind: 'mention'; personId: string; label: string }
  | { kind: 'link'; objectType: LinkObjectType; objectId: string; label: string };

/**
 * UUID-en valideres av FORMEN og ikke av et skjema, fordi en kropp med en ødelagt peker i seg ikke
 * skal avvises: teksten er det viktige, og en peker som ikke er en uuid er bare en tekst som ser
 * rar ut. Den faller da gjennom som `text` og vises som den ble skrevet.
 */
const UUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';

/**
 * ÉN regex for begge formene, fordi de må leses i den samme gjennomgangen: to separate søk over den
 * samme strengen kan ikke settes sammen igjen i riktig rekkefølge uten å sammenligne posisjoner,
 * og det er nøyaktig der en rundtur begynner å bytte om på ting.
 *
 * `[^\]\n]*` på etiketten: den kan ikke inneholde `]` (den ville avsluttet pekeren) og ikke et
 * linjeskift (en peker er én linje). Begge er tomme-tillatende, så `@[[person:…|]]` er en gyldig
 * peker uten navn — den tegnes med id-en som reserve framfor å bli borte.
 */
const TOKEN = new RegExp(
  `(@)\\[\\[person:(${UUID})\\|([^\\]\\n]*)\\]\\]` +
    `|(#)\\[\\[(task|performance|resource):(${UUID})\\|([^\\]\\n]*)\\]\\]`,
  'g',
);

/** Kroppen delt i biter, i den rekkefølgen de står. Kaster aldri. */
export function segmentsOf(body: string): ChorusMdSegment[] {
  const segments: ChorusMdSegment[] = [];
  let cursor = 0;

  TOKEN.lastIndex = 0;
  let match: RegExpExecArray | null = TOKEN.exec(body);
  while (match !== null) {
    if (match.index > cursor) {
      segments.push({ kind: 'text', text: body.slice(cursor, match.index) });
    }

    const personId = match[2];
    if (personId !== undefined) {
      segments.push({ kind: 'mention', personId: personId.toLowerCase(), label: match[3] ?? '' });
    } else {
      /* Regexen tillater bare de tre typene, så castet beskriver formen framfor å utvide den. */
      segments.push({
        kind: 'link',
        objectType: (match[5] ?? 'task') as LinkObjectType,
        objectId: (match[6] ?? '').toLowerCase(),
        label: match[7] ?? '',
      });
    }

    cursor = match.index + match[0].length;
    match = TOKEN.exec(body);
  }

  if (cursor < body.length) segments.push({ kind: 'text', text: body.slice(cursor) });
  return segments;
}

/** Bitene satt sammen igjen. `bodyOf(segmentsOf(x)) === x` for enhver `x`. */
export function bodyOf(segments: readonly ChorusMdSegment[]): string {
  return segments
    .map((segment) => {
      if (segment.kind === 'text') return segment.text;
      if (segment.kind === 'mention') return personMention(segment.personId, segment.label);
      return objectLink(segment.objectType, segment.objectId, segment.label);
    })
    .join('');
}

/**
 * Pekerne, bygget ett sted.
 *
 * Etiketten renses for `]` og linjeskift — de to tegnene som ville brutt formen — framfor å bli
 * avvist: en person som faktisk heter noe med en klammeparentes i skal kunne nevnes, og navnet
 * hentes uansett fra id-en når den kan slås opp.
 */
export function personMention(personId: string, label: string): string {
  return `@[[person:${personId}|${safeLabel(label)}]]`;
}

export function objectLink(objectType: LinkObjectType, objectId: string, label: string): string {
  return `#[[${objectType}:${objectId}|${safeLabel(label)}]]`;
}

function safeLabel(label: string): string {
  return label.replace(/[\]\r\n]/g, ' ').trim();
}

/**
 * Person-id-ene en kropp nevner, uten dubletter og i den rekkefølgen de står.
 *
 * Dette er den ene funksjonen serveren HANDLER på: hver id her blir en `message_mentions`-rad og et
 * varsel, etter at tjenesten har sjekket at personen er deltaker i tråden.
 */
export function mentionedPersonIds(body: string): string[] {
  const found: string[] = [];
  for (const segment of segmentsOf(body)) {
    if (segment.kind !== 'mention') continue;
    if (!found.includes(segment.personId)) found.push(segment.personId);
  }
  return found;
}

/** Objektene en kropp lenker til, uten dubletter og i rekkefølge. */
export function linkedObjects(body: string): { objectType: LinkObjectType; objectId: string }[] {
  const found: { objectType: LinkObjectType; objectId: string }[] = [];
  for (const segment of segmentsOf(body)) {
    if (segment.kind !== 'link') continue;
    const already = found.some(
      (one) => one.objectType === segment.objectType && one.objectId === segment.objectId,
    );
    if (!already) found.push({ objectType: segment.objectType, objectId: segment.objectId });
  }
  return found;
}

/**
 * Kroppen som REN TEKST — pekerne erstattet av etikettene sine.
 *
 * Innboksens forhåndsvisning og varselsetningen («Anders nevnte deg i …») trenger en linje uten
 * `[[`-skrap i. Den gjør IKKE noe med markdown ellers: en forhåndsvisning av «**hei**» skal vise
 * stjernene, fordi å fjerne dem her ville vært en halv renderer på feil sted.
 */
export function plainTextOf(body: string): string {
  return segmentsOf(body)
    .map((segment) => {
      if (segment.kind === 'text') return segment.text;
      if (segment.kind === 'mention') return segment.label.length > 0 ? `@${segment.label}` : '@?';
      return segment.label.length > 0 ? segment.label : '#?';
    })
    .join('');
}
